import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiCode, readApiMessages, readApiStatus } from "@/shared/utils/apiError";

/**
 * `code` of the 403 the payments runtime answers while the organization's own verification (KYC or KYB) is
 * not VERIFIED. The only signal that separates that 403 from a missing `payments:write`.
 */
export const ORGANIZATION_VERIFICATION_REQUIRED_CODE = "organization_verification_required";

/**
 * `code` of the 409 a payout create answers when the rail cannot pay that amount right now (Transfiya while
 * its float is short). Refused before anything is created or reserved, so the quote was not spent; the
 * message already names the rail to use instead.
 */
export const PROVIDER_BALANCE_INSUFFICIENT_CODE = "provider_balance_insufficient";

export function isProviderBalanceShort(error: unknown): boolean {
  return readApiCode(error) === PROVIDER_BALANCE_INSUFFICIENT_CODE;
}

/**
 * What a payments-runtime error can be classified as **from the client**, honestly.
 *
 * The list is short on purpose. The runtime throws ~146 exceptions across the payments module and only a
 * handful are built from an object, so only those reach the wire with a `code` — the corridor amount limits
 * and the organization verification gate — every other error is a status plus a sentence that is already
 * translated into the caller's language. There is no code taxonomy to map against, and matching the
 * translated sentence would mean copying three locales out of another repo with nothing to catch the drift
 * when someone edits the copy there.
 *
 * So this classifies only what is genuinely knowable, and hands back the backend's own sentence for the rest.
 * `unknown` is not a failure of the normaliser — it is the truthful answer most of the time, and the sentence
 * it carries IS localised.
 *
 * A caller that needs to distinguish further has to ASK the backend; the 409 on a payout is the case that
 * matters, and `useSubmitDispersion` resolves it by reading the quote's status afterwards.
 */
export type PaymentErrorKind =
  | "verification_required"
  | "access_denied"
  | "quote_expired"
  | "validation"
  | "network"
  | "unknown";

export interface NormalizedPaymentError {
  kind: PaymentErrorKind;
  status: number | null;
  /**
   * Machine-readable detail when the backend sent one (`organization_verification_required`, the amount-limit
   * codes, a payin's provider code).
   */
  code: string | null;
  /** One sentence per broken rule. Never empty when the server answered. */
  messages: string[];
  /** The sentences joined, for a caller that renders a single line. */
  message: string;
}

export interface PaymentErrorContext {
  /** The quote the failed call was redeeming, if any. Lets expiry be decided locally instead of guessed. */
  quoteExpiresAt?: string | null;
  /** Injected so the decision is testable and matches whatever clock the caller already used. */
  now?: number;
}

function readCode(error: unknown): string | null {
  if (!HttpApiService.isAxiosError(error)) return null;
  const payload = error.response?.data as { code?: unknown } | undefined;
  return typeof payload?.code === "string" && payload.code.length > 0 ? payload.code : null;
}

/** Whether the request never got an answer — distinct from an answer that says no. */
function isNetworkFailure(error: unknown): boolean {
  return HttpApiService.isAxiosError(error) && error.response === undefined;
}

/**
 * The `ValidationPipe` signature: it rejects with one sentence per broken rule, and only it does.
 *
 * Worth separating because those sentences are the ONE class of backend message that is never translated —
 * `@Matches` messages on the quote DTO are English literals, not i18n keys — and because rendering the array
 * as a single string is what produces the run-on text users see today.
 */
function isValidationRejection(error: unknown, messages: string[]): boolean {
  if (!HttpApiService.isAxiosError(error)) return false;
  const raw = (error.response?.data as { message?: unknown } | undefined)?.message;
  return readApiStatus(error) === 400 && Array.isArray(raw) && messages.length > 0;
}

export function normalizePaymentError(
  error: unknown,
  context: PaymentErrorContext = {},
): NormalizedPaymentError {
  const status = readApiStatus(error);
  const messages = readApiMessages(error);
  const code = readCode(error);

  const kind: PaymentErrorKind = (() => {
    // Ahead of the access check, and the order matters: this is a 403 too, and without the `code` it would be
    // read as "no permission" — the opposite remedy (verify the account, not the API key or the role).
    if (code === ORGANIZATION_VERIFICATION_REQUIRED_CODE) return "verification_required";

    if (HttpApiService.isAccessDeniedError(error)) return "access_denied";

    // Ahead of the expiry check, and the order matters. A `ValidationPipe` rejection means the request never
    // reached the service, so the quote was not touched and the BODY is what is wrong. Re-pricing does not fix
    // a malformed field: the operator would get a fresh quote, resubmit the same body and be sent to re-quote
    // again. Reporting the field is the only exit.
    if (isValidationRejection(error, messages)) return "validation";

    // Decided from the quote in hand, not from the response: the runtime answers `quote_expired` as a plain
    // 400 with a sentence, indistinguishable from half a dozen other 400s. Past this point the quote has
    // lapsed and nothing else can succeed until it is re-priced, whatever the server actually complained
    // about — so expiry outranks the rest.
    //
    // Only for errors the SERVER produced. A locally-thrown Error carries a verdict we already reached and
    // must survive it: a payout the provider rejected (create answers 201 with status FAILED, which the
    // wizard turns into a throw) would otherwise be reported as an expired quote if the price happened to
    // lapse mid-request, hiding a terminal rejection behind a re-quote prompt.
    const expiresAt = HttpApiService.isAxiosError(error) ? context.quoteExpiresAt : null;
    if (expiresAt) {
      const expiry = Date.parse(expiresAt);
      if (!Number.isNaN(expiry) && expiry <= (context.now ?? Date.now())) return "quote_expired";
    }

    if (isNetworkFailure(error)) return "network";
    return "unknown";
  })();

  return {
    kind,
    status,
    code,
    messages,
    // Falls back to `getErrorMessage` only when the server sent nothing to say — on a network failure that
    // yields axios's own "Request failed with status code…", which is why `network` is a kind: the caller
    // should show its own copy rather than that.
    message: messages.join(" ") || HttpApiService.getErrorMessage(error),
  };
}
